import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import ConfirmationPopup from "../ConfirmationPopup";
import {
  TrashIcon,
  PlusGrayIcon,
  RedExclamationCircle,
} from "../../assets/svgs";
import useApp from "../../hooks/useApp";
import "./styles.scss";
import DocumentUploadCard from "../UploadDocumentCard";
import { parseServerNameMeta } from "../../utils/document";
import { staticProviders } from "../SelectBankModal";

const normalize = (v = "") => String(v).trim().toLowerCase();

const DocumentSection = React.memo(function DocumentSection({
  title,
  subTitle,
  docSize,
  sectionKey,
  docDef,
  docCount,
  docsForThisTab,
  bankGroupKey,
  tabKey,
  tabGroupKey,
  providerId,
  fileUploadStatus,
  handleFileUpload,
  onDeleteDocument,
  isLoadingDocuments,
  onAllRequiredUploaded,
  t,
  routeName,
}) {
  const uiKey = useMemo(
    () => `${bankGroupKey}-${tabKey}-${sectionKey}`,
    [bankGroupKey, tabKey, sectionKey]
  );

  const slotDocs = useMemo(() => {
    if (!docDef) return [];
    return Array.from({ length: docCount }, () => ({ ...docDef }));
  }, [docDef, docCount]);

  const uploadedCount = useMemo(() => {
    const accepted = Array.isArray(docDef?.accetFileType)
      ? docDef.accetFileType
      : [];
    const acceptedSet = new Set(accepted.map(normalize));

    return (docsForThisTab || []).filter((d) => {
      const fmt = normalize(d?.format || "");
      return acceptedSet.size ? acceptedSet.has(fmt) : true;
    }).length;
  }, [docsForThisTab, docDef]);

  if (!docDef) return null;

  return (
    <div className="group-section">
      <div className="group-section-heading">
        <span>{title}</span>
        <span className="group-section-count">{`${uploadedCount}/${docCount}`}</span>
      </div>

      <div className="upload-file-size">
        {t(`${routeName}.${subTitle}`, {
          uploadedCount: uploadedCount,
          docCount: docCount,
          documentSize: docSize ?? 10,
        })}
      </div>

      <DocumentUploadCard
        documents={slotDocs}
        handleFileUpload={handleFileUpload}
        fileUploadStatus={fileUploadStatus}
        fileKey={uiKey}
        serverGroupKey={tabGroupKey}
        providerId={providerId}
        documentList={docsForThisTab}
        onDeleteDocument={onDeleteDocument}
        isLoadingDocuments={isLoadingDocuments}
        onAllRequiredUploaded={onAllRequiredUploaded}
      />
    </div>
  );
});

const BankGroupCard = React.memo(function BankGroupCard({
  bank,
  documents,
  tabs,
  t,
  routeName,
  fileUploadStatus,
  handleFileUpload,
  documentListForBank,
  onDeleteDocument,
  isLoadingDocuments,
  onGroupDeleteClick,
  onAllRequiredUploaded,
}) {
  const { handleResetFile } = useApp();

  const bankGroupKey = useMemo(
    () => `bank-${bank.providerId}`,
    [bank.providerId]
  );

  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const [showPopup, setShowPopup] = useState(false);
  const [pendingTabIndex, setPendingTabIndex] = useState(null);

  const activeTab = tabs?.[activeTabIndex];
  const tabKey = activeTab?.tabType || `tab_${activeTabIndex}`;
  const tabGroupKey = `${bankGroupKey}-${tabKey}`;

  const getTabDocuments = useCallback(
    (tabIndex) => {
      const tab = tabs?.[tabIndex];
      const tKey = tab?.tabType || `tab_${tabIndex}`;
      const expectedGroup = normalize(`${bankGroupKey}-${tKey}`);

      return (documentListForBank || []).filter((d) => {
        const meta = parseServerNameMeta(d?.name || "");
        return normalize(meta?.groupKey || "") === expectedGroup;
      });
    },
    [tabs, bankGroupKey, documentListForBank]
  );

  const currentTabDocs = useMemo(
    () => getTabDocuments(activeTabIndex),
    [getTabDocuments, activeTabIndex]
  );

  const docCount = useMemo(() => {
    if (!tabs?.length) return 1;
    const count = Number(tabs[activeTabIndex]?.documentCount);
    return Number.isFinite(count) && count > 0 ? count : 1;
  }, [tabs, activeTabIndex]);

  const sections = useMemo(() => {
    return (documents || []).map((d) => {
      const accepted = Array.isArray(d?.accetFileType)
        ? d.accetFileType.map(normalize)
        : [];
      const isPdf = accepted.includes("pdf");
      const isXls = accepted.includes("xls") || accepted.includes("xlsx");

      return {
        key: isPdf ? "pdf" : isXls ? "xls" : d.fileType || d.type,
        title: t(`${routeName}.${d?.section_label}`),
        subTitle: d?.section_sub_label,
        docSize: d?.maxSize,
        docDef: d,
      };
    });
  }, [documents]);

  const [sectionStatus, setSectionStatus] = useState({});

  const updateSectionStatus = useCallback(
    (sectionKey, status) => {
      const nextStatus = !!status;

      setSectionStatus((prev) => {
        if (prev[sectionKey] === nextStatus) return prev;

        const next = { ...prev, [sectionKey]: nextStatus };
        const allDone = sections.length
          ? sections.every((s) => next[s.key] === true)
          : true;

        onAllRequiredUploaded?.(bankGroupKey, allDone);

        return next;
      });
    },
    [sections, onAllRequiredUploaded, bankGroupKey]
  );

  const handleTabClick = (nextIndex) => {
    if (nextIndex === activeTabIndex) return;

    const docsInCurrent = currentTabDocs;

    if (docsInCurrent.length > 0) {
      setPendingTabIndex(nextIndex);
      setShowPopup(true);
      return;
    }

    setActiveTabIndex(nextIndex);
  };

  const handleOnClickCancel = () => {
    setShowPopup(false);
    setPendingTabIndex(null);
  };

  const handleOnClickConfirm = async () => {
    try {
      const docsToDelete = currentTabDocs;

      const keysToReset = docsToDelete
        .map((doc) => {
          const meta = parseServerNameMeta(doc?.name || "");

          const format = normalize(doc?.format || "");
          const sectionKey = format === "pdf" ? "pdf" : "xls";

          const uiKeyPrefix = `${bankGroupKey}-${tabKey}-${sectionKey}`;
          return `${uiKeyPrefix}-${doc.document_type}-${meta.slotIndex}`;
        })
        .filter(Boolean);

      for (const doc of docsToDelete) {
        await onDeleteDocument?.(doc.id);
      }

      keysToReset.forEach((k) => handleResetFile?.(k));

      onAllRequiredUploaded?.(tabGroupKey, false);

      const next = pendingTabIndex;
      setPendingTabIndex(null);
      setShowPopup(false);

      if (typeof next === "number") setActiveTabIndex(next);
    } catch (e) {
      setShowPopup(false);
      setPendingTabIndex(null);
    }
  };

  const isTabSectionComplete = useCallback(
    (sectionKey) => {
      const def = sections.find((s) => s.key === sectionKey)?.docDef;
      if (!def) return true;

      const accepted = Array.isArray(def.accetFileType)
        ? def.accetFileType.map(normalize)
        : [];
      const acceptedSet = new Set(accepted);

      const uploaded = (currentTabDocs || []).filter((d) => {
        const fmt = normalize(d?.format || "");
        return acceptedSet.size ? acceptedSet.has(fmt) : true;
      }).length;

      return uploaded >= docCount;
    },
    [sections, currentTabDocs, docCount]
  );

  useEffect(() => {
    if (!sections.length) return;

    const next = {};
    sections.forEach((s) => {
      next[s.key] = isTabSectionComplete(s.key);
    });

    setSectionStatus(next);

    const allDone = sections.every((s) => next[s.key] === true);
    onAllRequiredUploaded?.(bankGroupKey, allDone);
  }, [
    bankGroupKey,
    tabKey,
    sections,
    isTabSectionComplete,
    onAllRequiredUploaded,
  ]);

  return (
    <div className="group-card" key={bankGroupKey}>
      <div className="group-card-header">
        <span className="group-card-label">{`${bank.displayName} ${t(`${routeName}.bank`)}`}</span>
        <img
          src={TrashIcon}
          alt="delete"
          className="group-card-icon"
          onClick={() => onGroupDeleteClick(bank)}
        />
      </div>

      {tabs?.length > 0 && (
        <div className="group-tabs">
          <div className="group-tabs-title">
            {t(`${routeName}.how_did_you_extract_your_bank_statement`)}
          </div>

          <div className="tab-bar-container">
            {tabs?.map((item, index) => (
              <div
                key={index}
                className={`tab-item ${activeTabIndex === index ? "tab-item-active" : ""}`}
                onClick={() => handleTabClick(index)}
              >
                {t(`${routeName}.${item.tabType}`)}
              </div>
            ))}
          </div>
        </div>
      )}

      {sections.map((s) => (
        <DocumentSection
          key={`${bankGroupKey}-${tabKey}-${s.key}`}
          title={s.title}
          subTitle={s.subTitle}
          docSize={s.docSize}
          sectionKey={s.key}
          docDef={s.docDef}
          docCount={docCount}
          docsForThisTab={currentTabDocs}
          bankGroupKey={bankGroupKey}
          tabKey={tabKey}
          tabGroupKey={tabGroupKey}
          providerId={bank.providerId}
          fileUploadStatus={fileUploadStatus}
          handleFileUpload={handleFileUpload}
          onDeleteDocument={onDeleteDocument}
          isLoadingDocuments={isLoadingDocuments}
          onAllRequiredUploaded={(status) => updateSectionStatus(s.key, status)}
          t={t}
          routeName={routeName}
        />
      ))}

      <ConfirmationPopup
        show={showPopup}
        icon={RedExclamationCircle}
        heading="remove_tab_popup.heading"
        description="remove_tab_popup.description"
        cancelButtonText="global.cancel"
        onClickCancel={handleOnClickCancel}
        confirmButtonText="global.confirm"
        onClickConfirm={handleOnClickConfirm}
      />
    </div>
  );
});

const DocumentGroupUploadCard = ({
  documents = [],
  handleFileUpload,
  fileUploadStatus = {},
  onClickAddBank,
  onAllRequiredUploaded,
  documentList = [],
  onDeleteDocument,
  isLoadingDocuments = false,
}) => {
  const { selectedBanks, handleSelectedBanks, handleResetGroupFiles } =
    useApp();
  const { t } = useTranslation();
  const location = useLocation();
  const { pathname } = location;
  const routeName = pathname.split("/")[1];

  const [showPopup, setShowPopup] = useState(false);
  const [providerIdToDelete, setProviderIdToDelete] = useState(null);

  const [activeTabByBank, setActiveTabByBank] = useState({});

  const [bankSectionStatus, setBankSectionStatus] = useState({});

  const getBankDocumentList = useCallback(
    (bank) => {
      const pid = normalize(bank?.providerId);
      if (!pid) return documentList;
      return (documentList || []).filter((d) => {
        const meta = parseServerNameMeta(d?.name || "");
        const metaPid = normalize(meta?.providerId || "");
        const pid = normalize(bank?.providerId || "");

        const metaPidBase = metaPid.split("-")[0];

        return metaPid === pid || metaPidBase === pid;
      });
    },
    [documentList]
  );

  const getBankDocuments = useCallback(
    (providerId) => {
      const pid = normalize(providerId);
      return (documentList || []).filter((doc) => {
        const meta = parseServerNameMeta(doc.name);
        return normalize(meta?.providerId) === pid;
      });
    },
    [documentList]
  );

  const tabs = useMemo(() => {
    const tbs = documents?.[0]?.tabs;
    if (!Array.isArray(tbs)) return [];
    return [...tbs].sort((a, b) => Number(a.tabIndex) - Number(b.tabIndex));
  }, [documents]);

  const getActiveTabIndex = useCallback(
    (bankGroupKey) => {
      const v = activeTabByBank[bankGroupKey];
      return typeof v === "number" ? v : 0;
    },
    [activeTabByBank]
  );

  const getDocCountForBank = useCallback(
    (bankGroupKey) => {
      if (!tabs.length) return 1;
      const idx = getActiveTabIndex(bankGroupKey);
      const count = Number(tabs[idx]?.documentCount);
      return Number.isFinite(count) && count > 0 ? count : 1;
    },
    [tabs, getActiveTabIndex]
  );

  useEffect(() => {
    if (!documentList?.length) return;
    if (selectedBanks?.length) return;

    const providerIdsFromDocs = Array.from(
      new Set(
        documentList
          .map((d) => parseServerNameMeta(d.name)?.providerId)
          .filter(Boolean)
          .map((p) => normalize(p))
      )
    );

    const initialBanks = staticProviders.filter((bank) =>
      providerIdsFromDocs.includes(normalize(bank.providerId))
    );

    if (initialBanks.length) {
      handleSelectedBanks(initialBanks);
    }
  }, [documentList, selectedBanks?.length, handleSelectedBanks]);

  const handleOnClickGroupDelete = (bank) => {
    setShowPopup(true);
    setProviderIdToDelete(bank.providerId);
  };

  const handleOnClickCancel = () => {
    setShowPopup(false);
    setProviderIdToDelete(null);
  };

  const handleOnClickConfirm = async () => {
    if (!providerIdToDelete) return;

    const bankGroupKey = `bank-${providerIdToDelete}`;
    const bankDocs = getBankDocuments(providerIdToDelete);

    for (const doc of bankDocs) {
      await onDeleteDocument?.(doc.id);
    }

    handleResetGroupFiles(bankGroupKey);

    handleSelectedBanks(
      selectedBanks.filter((b) => b.providerId !== providerIdToDelete)
    );

    if (onAllRequiredUploaded) {
      onAllRequiredUploaded(bankGroupKey, true);
    }

    setShowPopup(false);
    setProviderIdToDelete(null);
  };

  const updateSectionStatus = useCallback(
    (bankGroupKey, sectionKey, status) => {
      const nextStatus = !!status;

      setBankSectionStatus((prev) => {
        const prevBank = prev[bankGroupKey] || {};
        if (prevBank[sectionKey] === nextStatus) return prev;

        const nextBank = { ...prevBank, [sectionKey]: nextStatus };
        const next = { ...prev, [bankGroupKey]: nextBank };

        const values = Object.values(nextBank);
        const allUploaded = values.length ? values.every(Boolean) : true;

        onAllRequiredUploaded?.(bankGroupKey, allUploaded);
        return next;
      });
    },
    [onAllRequiredUploaded]
  );

  return (
    <div className="document-group-upload-card">
      {selectedBanks.map((bank) => (
        <BankGroupCard
          key={`bank-${bank.providerId}`}
          bank={bank}
          documents={documents}
          tabs={tabs}
          t={t}
          routeName={routeName}
          fileUploadStatus={fileUploadStatus}
          handleFileUpload={handleFileUpload}
          documentListForBank={getBankDocumentList(bank)}
          onDeleteDocument={onDeleteDocument}
          isLoadingDocuments={isLoadingDocuments}
          onGroupDeleteClick={handleOnClickGroupDelete}
          onAllRequiredUploaded={onAllRequiredUploaded}
        />
      ))}

      <div className="add-document-button" onClick={onClickAddBank}>
        <span className="button-icon">
          <img src={PlusGrayIcon} alt="" />
        </span>
        <span className="button-text">
          {t(`${routeName}.add_your_bank_statement`)}
        </span>
      </div>

      <ConfirmationPopup
        show={showPopup}
        icon={RedExclamationCircle}
        heading="remove_bank_popup.heading"
        description="remove_bank_popup.description"
        cancelButtonText="global.cancel"
        onClickCancel={handleOnClickCancel}
        confirmButtonText="global.confirm"
        onClickConfirm={handleOnClickConfirm}
      />
    </div>
  );
};

export default DocumentGroupUploadCard;
